/**
 * 구간별 요약 통계를 **출처별로 묶는** 순수 모델.
 *
 * Owns:
 * - 볼 수치가 없는 구간을 거른다(길이 0 경계 조각, 지표 0개, 기록 범위 밖).
 * - 구간을 action_source 출처에 귀속하고 출처별 길이 합을 계산한다.
 *
 * Does not own:
 * - 출처 어휘 판정(`../segments/actionSourceSegments`)과 이름·색(`../segments/actionSourceTerms`).
 * - 아티팩트 파싱(`./timelineArtifact`), 채널 카드 기하(`./motionSummaryBars`).
 *
 * **출처 단위 통계를 만들지 않는다.**
 * - p95 는 표본 분위수라서 구간을 합쳐 다시 계산할 수 없다.
 * - RMS·평균은 시간 가중이라 합칠 수 있다. 그러나 한 표에 섞으면 어느 값을 합칠 수 있는지
 *   알 수 없다.
 * 길이 합만 낸다. 길이 합은 구간 길이의 합이라는 뜻이 분명하다.
 *
 * 걸러내는 이유(상류 실측, ZERG-618): 상류는 모든 상태 트랙 경계의 합집합으로 구간을 나눈다.
 * 그래서 채널마다 기록이 시작·종료되는 순간에 표본 0개인 밀리초 조각이 생긴다
 * (창원 실측 303개 중 182개). 상류를 고치는 기준에 맞는 경우는 실데이터에서 0건이었다.
 * 그래서 해석은 소비처인 registry 가 소유한다.
 */

import { ACTION_SOURCE_CHANNEL } from "@/api/diagnosticSegments";
import {
  ACTION_SOURCE_CATEGORY_ORDER,
  actionSourceCategoryOf,
  type ActionSourceCategory,
} from "../segments/actionSourceSegments";
import type { EpisodeTimeline, TimelineEvent, TimelineStateInterval } from "./timelineArtifact";

/**
 * 화면에 남은 구간 하나. `index` 는 아티팩트 `events` 안 위치(1부터)다.
 * 그래서 원본과 대조할 수 있다.
 */
export type TimelineSegment = {
  index: number;
  event: TimelineEvent;
  startSeconds: number;
  endSeconds: number;
};

export type TimelineSourceGroup = {
  /** 출처 어휘의 범주. 아티팩트가 이 구간의 출처를 기록하지 않았으면 null. */
  category: ActionSourceCategory | null;
  /** 원본 상태 문자열. `unknown` 은 값마다 다른 묶음이라서 이름에 그대로 표시한다. */
  state: string | null;
  segments: readonly TimelineSegment[];
  /** 이 출처 구간들의 길이 합. 합칠 수 있는 유일한 값이다. */
  totalSeconds: number;
};

export type TimelineSegmentGrouping = {
  groups: readonly TimelineSourceGroup[];
  /** 아티팩트가 준 구간 수와 화면에 남은 수. 캡션이 몇 개를 걸렀는지 알린다. */
  total: number;
  shown: number;
};

/**
 * 이 구간을 덮는 action_source 상태. 구간은 경계마다 잘리므로 상태는 보통 하나다.
 * 여럿이면 가장 긴 것을 고른다.
 */
function sourceInterval(event: TimelineEvent): TimelineStateInterval | null {
  const intervals = event.states.get(ACTION_SOURCE_CHANNEL) ?? [];
  let longest: TimelineStateInterval | null = null;
  for (const interval of intervals) {
    if (longest === null || interval.tEnd - interval.tStart > longest.tEnd - longest.tStart) {
      longest = interval;
    }
  }
  return longest;
}

/**
 * 볼 수치가 있는 구간인가.
 *
 * `reason` 이 있는 `unknown` 은 정의 밖 값이 아니다. **그 채널의 기록 범위 밖**이다
 * (`outside_coverage`). 이것은 값이 아니라 경계라서 화면에 줄로 표시할 것이 없다.
 */
function hasNumbers(event: TimelineEvent, interval: TimelineStateInterval | null): boolean {
  if (!(event.tEnd > event.tStart)) return false;
  if (interval !== null && interval.state === "unknown" && interval.reason !== null) return false;
  for (const channel of event.channels.values()) {
    if (channel.metrics.size > 0) return true;
  }
  return false;
}

// 출처 표시 순서. 기록이 없는 묶음(null)은 맨 뒤다.
function order(category: ActionSourceCategory | null): number {
  const index = category === null ? -1 : ACTION_SOURCE_CATEGORY_ORDER.indexOf(category);
  return index < 0 ? ACTION_SOURCE_CATEGORY_ORDER.length : index;
}

/** 구간을 걸러 출처별로 묶는다. 묶음 안 순서는 시간순(아티팩트 순서)이다. */
export function timelineSegmentGroups(timeline: EpisodeTimeline): TimelineSegmentGrouping {
  const groups = new Map<string, TimelineSourceGroup & { segments: TimelineSegment[] }>();
  let shown = 0;
  timeline.events.forEach((event, index) => {
    const interval = sourceInterval(event);
    if (!hasNumbers(event, interval)) return;
    shown += 1;
    const state = interval?.state ?? null;
    const category = state === null ? null : actionSourceCategoryOf(state);
    // `unknown` 은 값마다 다른 묶음이다. 정의 밖 값 두 개를 한 줄로 합치면 서로 다른 값이
    // 같은 출처로 읽힌다.
    const key = category === null ? "" : category === "unknown" ? `unknown:${state}` : category;
    const group = groups.get(key) ?? { category, state, segments: [], totalSeconds: 0 };
    group.segments.push({
      index: index + 1,
      event,
      startSeconds: event.tStart,
      endSeconds: event.tEnd,
    });
    group.totalSeconds += event.tEnd - event.tStart;
    groups.set(key, group);
  });
  return {
    groups: [...groups.values()].sort((a, b) => order(a.category) - order(b.category)),
    total: timeline.events.length,
    shown,
  };
}
